/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { Thumbnail } from '@mpb-com/feather/components/atoms/thumbnail';
import { TFunction, Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';
import styled from 'styled-components';

import { getModelHrefFromSlug } from '@toucan/common/services/model/helpers';

import { CloudflareImageSize } from '@toucan/client/global/cloudflare';
import { MarketContext } from '@toucan/client/global/market';
import { fallbackImagePath } from '@toucan/client/global/product';

import { Card } from '@toucan/client/shared/components/card';
import { Link, LinkTarget } from '@toucan/client/shared/components/link';

import { CloudflareImage } from '@toucan/client/blocks/components/cloudflare-image';

import { formatShareAsPercentage } from '../../../helpers';
import { TimelineSide } from '../../config/constants';
import { TimelineSelectionShare } from '../../config/types';
import { DEFAULT_TEST_ID } from './config/constants';
import { TimelineDetailProps } from './config/types';
import { styles } from './timeline-detail.styles';

export * from './config/constants';
export type { TimelineDetailProps } from './config/types';

const StyledTimelineDetail = styled(Card)`
    ${styles}
`;

/**
 * What this model's shares say, as one sentence.
 *
 * The two sides count different people — before the marker it is a share of
 * everyone who bought the model being viewed, after it a share of everyone who
 * owns it — so a model in both hands has two shares to report. They are joined
 * into a single sentence rather than listed, and each case is a whole phrase in
 * the translation files rather than fragments stitched together at runtime.
 */
function getTimelineDetailShareSentence(
    t: TFunction,
    formatShare: (pct: number) => string,
    shares: readonly TimelineSelectionShare[]
): string {
    const before = shares.find(({ side }) => side === TimelineSide.BEFORE);
    const after = shares.find(({ side }) => side === TimelineSide.AFTER);

    if (before && after) {
        return t(
            '<share-value>{{before}}</share-value> of buyers owned this one first, and <share-value>{{after}}</share-value> of owners bought this one next',
            {
                before: formatShare(before.pct),
                after: formatShare(after.pct),
            }
        );
    }

    if (before) {
        return t(
            '<share-value>{{share}}</share-value> of buyers owned this one first',
            { share: formatShare(before.pct) }
        );
    }

    return t(
        '<share-value>{{share}}</share-value> of owners bought this one next',
        { share: formatShare(after?.pct ?? 0) }
    );
}

export const TimelineDetail: FunctionComponent<TimelineDetailProps> = ({
    selection,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    /*
     * Nothing at all until a card is picked, rather than a card holding a prompt
     * to pick one. The fan is what a reader is looking at and the cards are
     * plainly pressable, so the prompt was explaining something the section had
     * already made obvious, and doing it in the space the answer goes.
     *
     * The cost is that picking a card grows the section rather than filling a
     * space already held open. That is covered: the press scrolls the detail into
     * view (see FanSection), which it had to do anyway.
     */
    if (!selection) {
        return null;
    }

    const { model, shares } = selection;
    const { name, urlSegment, imageUrl, longDescription } = model;
    const shareSentence = getTimelineDetailShareSentence(
        t,
        // The upstream reports a whole percentage; the formatter takes a
        // proportion.
        (pct) => formatShareAsPercentage(activeMarket.iso, pct / 100),
        shares
    );
    const description =
        longDescription ??
        t('We do not have a description for this model yet.');

    return (
        <StyledTimelineDetail data-testid={dataTestId} {...otherProps}>
            <Thumbnail
                className="timeline-detail-thumbnail"
                aspectRatio="4/3"
                height="12rem"
            >
                <CloudflareImage
                    /*
                     * Keyed on the image so picking a different model replaces
                     * this component rather than re-rendering it.
                     *
                     * CloudflareImage tracks which of its candidate sources it
                     * has fallen back to in `useFallback`, and that hook takes
                     * its sources once on mount and deliberately ignores them
                     * changing afterwards. Without a key it would therefore keep
                     * showing the first model's image for every model picked
                     * after it. Remounting resets that state, which is the fix
                     * available here — the hook is shared with the rest of the
                     * site, so it is not this experiment's to change.
                     */
                    key={imageUrl}
                    src={imageUrl ?? fallbackImagePath}
                    fallbackSrc={fallbackImagePath}
                    alt=""
                    imageSize={CloudflareImageSize.THUMBNAIL}
                />
            </Thumbnail>
            <div className="timeline-detail-body">
                <Heading
                    size={HeadingSize.H4}
                    as="h3"
                    className="timeline-detail-name"
                >
                    {name}
                </Heading>
                {/*
                 * Set at the heading size one step below the model name above it,
                 * so the numbers carry as the card's headline finding. Rendered as
                 * a paragraph rather than an h4: it is a sentence of prose, and a
                 * sentence in the heading outline is something a screen reader user
                 * navigating by heading has to read past.
                 */}
                <Heading
                    size={HeadingSize.H5}
                    as="p"
                    className="timeline-detail-share"
                    data-testid={`${dataTestId}__caption`}
                >
                    <Trans
                        components={{
                            'share-value': (
                                <span className="timeline-detail-share-value" />
                            ),
                        }}
                    >
                        {shareSentence}
                    </Trans>
                </Heading>
                <p
                    className="timeline-detail-text"
                    data-testid={`${dataTestId}__description`}
                >
                    {description}
                </p>
                <Link
                    className="timeline-detail-link"
                    href={getModelHrefFromSlug(
                        t,
                        activeMarket.slug,
                        urlSegment
                    )}
                    target={LinkTarget.BLANK}
                    rel="noopener noreferrer"
                    hasArrow
                    data-testid={`${dataTestId}__link`}
                >
                    {t('View this model')}
                </Link>
            </div>
        </StyledTimelineDetail>
    );
};
